import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiHeart, FiX } from "react-icons/fi";
import { FaThumbtack } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../context/TranslationContext";
import { addAnnouncementComment, toggleCommentLike, toggleCommentPin } from "../../api/announcements";
import { splitMentions } from "../../utils/mentions";
import { ownerName } from "../discover/campaignDisplay";
import UserProfileLink from "../UserProfileLink";
import MentionComposer from "./MentionComposer";

/** Replies shown before a longer thread folds behind "View N replies". */
const VISIBLE_REPLIES = 2;

const idOf = (v) => String(v?._id ?? v ?? "");

/**
 * A thread's replies in reading order: each one right after what it answers, so a
 * conversation inside the thread stays together. `answered` is the reply it answers,
 * if it answers one.
 */
const readingOrder = (replies, threadId) => {
  const byId = new Map(replies.map((reply) => [idOf(reply._id), reply]));
  const answers = new Map();
  for (const reply of replies) {
    const target = byId.has(idOf(reply.replyToId)) ? idOf(reply.replyToId) : threadId;
    if (!answers.has(target)) answers.set(target, []);
    answers.get(target).push(reply);
  }
  const walk = (id) =>
    (answers.get(id) || []).flatMap((reply) => [
      { reply, answered: byId.get(idOf(reply.replyToId)) || null },
      ...walk(idOf(reply._id)),
    ]);
  return walk(threadId);
};

/**
 * A post's comments: threads of replies, likes, one pinned comment on top, and
 * @mentions. Shared by the home feed and a Mint Space's own feed.
 *
 * The post belongs to the parent's list, so every change goes back through
 * `onPostChange` — first optimistically where it can, then as the server returned it.
 */
const PostComments = ({
  post,
  isDark,
  canPin = false,
  mintSpaceId,
  onPostChange,
  setToast,
  highlightCommentId = null,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null); // the comment being answered
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState({}); // thread id → shows every reply
  const [flash, setFlash] = useState(null);
  const inputRef = useRef(null);
  const me = idOf(user?.id || user?._id);

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";

  const comments = useMemo(() => post.comments || [], [post.comments]);
  const pinnedId = post.pinnedComment ? idOf(post.pinnedComment) : null;

  const threads = useMemo(() => {
    const ids = new Set(comments.map((c) => idOf(c._id)));
    const isTop = (c) => !c.parentId || !ids.has(idOf(c.parentId));
    const replies = new Map();
    for (const c of comments) {
      if (isTop(c)) continue;
      const parent = idOf(c.parentId);
      if (!replies.has(parent)) replies.set(parent, []);
      replies.get(parent).push(c);
    }
    const top = comments.filter(isTop);
    const pinned = top.find((c) => idOf(c._id) === pinnedId);
    const ordered = pinned ? [pinned, ...top.filter((c) => c !== pinned)] : top;
    return ordered.map((comment) => ({
      comment,
      replies: readingOrder(replies.get(idOf(comment._id)) || [], idOf(comment._id)),
    }));
  }, [comments, pinnedId]);

  // Arriving from a notification: open the thread, scroll to the comment, flash it.
  useEffect(() => {
    if (!highlightCommentId) return undefined;
    const target = comments.find((c) => idOf(c._id) === String(highlightCommentId));
    if (!target) return undefined;
    if (target.parentId) setExpanded((e) => ({ ...e, [idOf(target.parentId)]: true }));
    setFlash(String(highlightCommentId));
    const scroll = setTimeout(() => {
      document
        .getElementById(`comment-${highlightCommentId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    const clear = setTimeout(() => setFlash(null), 2600);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
    // Once per highlighted comment, not on every change to the thread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightCommentId]);

  const timeAgo = (iso) => {
    const date = new Date(iso);
    const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return t("time.justNow");
    if (minutes < 60) return t("comments.minutesShort", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("comments.hoursShort", { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 7) return t("comments.daysShort", { count: days });
    if (days < 365) return t("comments.weeksShort", { count: Math.floor(days / 7) });
    return date.toLocaleDateString();
  };

  const likedByMe = (c) => (c.likes || []).some((l) => idOf(l) === me);

  const handleLike = async (comment) => {
    const id = idOf(comment._id);
    const before = post;
    const was = likedByMe(comment);
    onPostChange({
      ...post,
      comments: comments.map((c) =>
        idOf(c._id) !== id
          ? c
          : { ...c, likes: was ? (c.likes || []).filter((l) => idOf(l) !== me) : [...(c.likes || []), me] }
      ),
    });
    try {
      const res = await toggleCommentLike(post._id, id, mintSpaceId);
      if (res?.success) onPostChange(res.data);
    } catch (error) {
      console.error("Error liking comment:", error);
      onPostChange(before);
      setToast?.(t("comments.likeFailed"));
    }
  };

  const handlePin = async (comment) => {
    try {
      const res = await toggleCommentPin(post._id, idOf(comment._id), mintSpaceId);
      if (res?.success) {
        onPostChange(res.data);
        setToast?.(res.pinned ? t("comments.pinnedToast") : t("comments.unpinnedToast"));
      }
    } catch (error) {
      console.error("Error pinning comment:", error);
      setToast?.(t("comments.pinFailed"));
    }
  };

  const startReply = (comment) => {
    setReplyTo(comment);
    // The box moves under this comment, so it's only there on the next frame.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(draft.length, draft.length);
    });
  };

  const jumpTo = (id) => {
    document.getElementById(`comment-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    setFlash(id);
    setTimeout(() => setFlash(null), 2600);
  };

  const submit = async () => {
    const text = draft.trim();
    if (!text || submitting) return;
    setSubmitting(true);
    try {
      const res = await addAnnouncementComment(
        post._id,
        text,
        mintSpaceId,
        replyTo ? idOf(replyTo._id) : null
      );
      if (res?.success) {
        onPostChange(res.data);
        if (replyTo) {
          const thread = replyTo.parentId ? idOf(replyTo.parentId) : idOf(replyTo._id);
          setExpanded((e) => ({ ...e, [thread]: true }));
        }
        setDraft("");
        setReplyTo(null);
      }
    } catch (error) {
      console.error("Error commenting:", error);
      setToast?.(t("toasts.failedAddComment"));
    } finally {
      setSubmitting(false);
    }
  };

  const composer = (
    <MentionComposer
      postId={post._id}
      value={draft}
      onChange={setDraft}
      onSubmit={submit}
      submitting={submitting}
      placeholder={replyTo ? t("comments.replyPlaceholder") : t("actualHome.addComment")}
      sendLabel={t("actualHome.postComment")}
      isDark={isDark}
      inputRef={inputRef}
    />
  );

  const renderComment = (c, isReply, answered = null) => {
    const id = idOf(c._id);
    const isPinned = !isReply && id === pinnedId;
    const likeCount = (c.likes || []).length;
    const liked = likedByMe(c);
    return (
      <div
        id={`comment-${id}`}
        className={`rounded-radius-sm transition-shadow duration-500 ${
          flash === id ? `ring-2 ring-mint/60 ${isDark ? "ring-offset-slate-900" : "ring-offset-white"} ring-offset-2` : ""
        }`}
      >
        {isPinned && (
          <div className="mb-1 inline-flex items-center gap-1 text-caption-1-semibold text-accent-text">
            <FaThumbtack className="size-2.5" />
            {t("comments.pinnedLabel")}
          </div>
        )}
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <UserProfileLink
            user={c.user}
            avatarSize={isReply ? "sm" : "md"}
            isDark={isDark}
            nameClassName={`text-subheadline-semibold ${baseText}`}
            fallback={c.user?.username || c.user?.businessName || t("common.user")}
          />
          <span className={`text-footnote ${mutedText}`}>{timeAgo(c.createdAt)}</span>
        </div>
        {answered && (
          <button
            type="button"
            onClick={() => jumpTo(idOf(answered._id))}
            className="mt-1 flex w-full flex-col items-start rounded-radius-xs border-l-2 border-accent-default bg-fill-quaternary px-2 py-1 text-left transition-colors hover:bg-fill-tertiary"
          >
            <span className="text-caption-1-semibold text-accent-text">
              {t("comments.repliedTo", { name: ownerName(answered.user) })}
            </span>
            <span className={`w-full truncate text-caption-1 ${mutedText}`}>{answered.content}</span>
          </button>
        )}
        <p className={`mt-0.5 whitespace-pre-wrap break-words text-subheadline leading-snug ${baseText}`}>
          {splitMentions(c.content, c.mentions).map((part, i) =>
            typeof part === "string" ? (
              part
            ) : (
              <button
                key={i}
                type="button"
                onClick={() => navigate(`/profile/${idOf(part.user._id)}`)}
                className="font-semibold text-accent-text hover:underline"
              >
                {part.text}
              </button>
            )
          )}
        </p>
        <div className={`mt-1 flex items-center gap-4 text-footnote-semibold ${mutedText}`}>
          <button
            type="button"
            onClick={() => handleLike(c)}
            aria-pressed={liked}
            aria-label={liked ? t("comments.unlike") : t("comments.like")}
            className={`inline-flex items-center gap-1 transition hover:text-status-danger ${liked ? "text-status-danger" : ""}`}
          >
            <FiHeart className={`size-3.5 ${liked ? "fill-current" : ""}`} />
            {likeCount > 0 && <span className="tabular-nums">{likeCount}</span>}
          </button>
          <button type="button" onClick={() => startReply(c)} className="transition hover:text-accent-text">
            {t("comments.reply")}
          </button>
          {canPin && !isReply && (
            <button
              type="button"
              onClick={() => handlePin(c)}
              className="inline-flex items-center gap-1 transition hover:text-accent-text"
            >
              <FaThumbtack className="size-2.5" />
              {isPinned ? t("comments.unpin") : t("comments.pin")}
            </button>
          )}
        </div>
        {idOf(replyTo?._id) === id && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center justify-between gap-2 rounded-radius-xs bg-fill-tertiary px-3 py-1.5 text-footnote text-label-secondary">
              <span className="min-w-0 truncate">{t("comments.replyingTo", { name: ownerName(replyTo.user) })}</span>
              <button
                type="button"
                onClick={() => setReplyTo(null)}
                aria-label={t("comments.cancelReply")}
                className="shrink-0 rounded p-0.5 transition hover:text-status-danger"
              >
                <FiX className="size-3.5" />
              </button>
            </div>
            {composer}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-3">
      {threads.length === 0 ? (
        <p className={`text-center text-subheadline ${mutedText}`}>{t("actualHome.noCommentsYet")}</p>
      ) : (
        <ul className="space-y-4">
          {threads.map(({ comment, replies }) => {
            const threadId = idOf(comment._id);
            const showReplies = expanded[threadId] || replies.length <= VISIBLE_REPLIES;
            return (
              <li key={threadId}>
                {renderComment(comment, false)}
                {replies.length > 0 && (
                  <div className={`ml-4 mt-2 space-y-3 border-l-2 pl-3 sm:ml-5 sm:pl-4 border-separator-default`}>
                    {showReplies ? (
                      replies.map(({ reply, answered }) => (
                        <div
                          key={idOf(reply._id)}
                          className={answered ? "ml-3 border-l-2 border-separator-default pl-3" : ""}
                        >
                          {renderComment(reply, true, answered)}
                        </div>
                      ))
                    ) : (
                      <button
                        type="button"
                        onClick={() => setExpanded((e) => ({ ...e, [threadId]: true }))}
                        className={`text-footnote-semibold transition hover:text-accent-text ${mutedText}`}
                      >
                        {t("comments.viewReplies", { count: replies.length })}
                      </button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!replyTo && composer}
    </div>
  );
};

export default PostComments;
